import { Box, Typography } from "@mui/material";
import { useState, useEffect } from "react";
import AreaBrowsePickerDialog from "./AreaBrowsePickerDialog";
import { STATE_TO_AREAS } from "../constants/malaysiaLocations";

// Which state does a given area belong to?
const AREA_TO_STATE = {};
Object.entries(STATE_TO_AREAS).forEach(([state, areas]) => {
  areas.forEach((area) => {
    AREA_TO_STATE[area] = state;
  });
});

export const AreaFilter = ({
  value,
  tempCurrentArea,
  onChange,
  onGetCurrentLocation,
  // kept for API compatibility — no longer used
  areaExpanded,
  onToggleExpanded,
  areaButtonText,
  isMobile = false,
}) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("state"); // "state" | "area"
  const [browsingState, setBrowsingState] = useState("");
  const [query, setQuery] = useState("");
  const [locationMode, setLocationMode] = useState("manual");

  // Determine location mode from value
  useEffect(() => {
    if (value === "current" || (value === "" && tempCurrentArea)) {
      setLocationMode("current");
    } else if (value && value !== "current") {
      setLocationMode("manual");
    } else {
      setLocationMode("manual");
    }
  }, [value, tempCurrentArea]);

  const handleOpen = () => {
    // If an area is already selected, pre-navigate to its state
    if (value && value !== "current" && AREA_TO_STATE[value]) {
      setBrowsingState(AREA_TO_STATE[value]);
      setStep("area");
    } else {
      setStep("state");
      setBrowsingState("");
    }
    setQuery("");
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setQuery("");
    setStep("state");
    setBrowsingState("");
  };

  const handleUseCurrentLocation = () => {
    setLocationMode("current");
    handleClose();
    onGetCurrentLocation();
  };

  const handleSelectState = (state) => {
    setBrowsingState(state);
    setStep("area");
    setQuery("");
  };

  const handleSelectArea = (area) => {
    onChange(area);
    setLocationMode("manual");
    handleClose();
  };

  const handleBack = () => {
    setStep("state");
    setBrowsingState("");
    setQuery("");
  };

  const selectedArea = value && value !== "current" ? value : null;
  const selectedState = selectedArea ? AREA_TO_STATE[selectedArea] : null;

  const isCurrentActive = locationMode === "current";
  const isManualActive = locationMode === "manual" && !!selectedArea;

  return (
    <Box
      sx={{
        py: isMobile ? 2 : 1.5,
        borderBottom: isMobile ? "none" : "1px solid #ddd",
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          mb: isMobile ? 1 : 1.25,
          color: "#000",
          fontSize: isMobile ? "16px" : undefined,
          fontWeight: isMobile ? 500 : undefined,
        }}
      >
        Area
      </Typography>
      {/* <Typography
        variant="caption"
        component="p"
        sx={{
          m: 0,
          mb: isMobile ? 1 : 1.25,
          color: "#666",
          fontSize: isMobile ? "13px" : "12px",
          lineHeight: 1.35,
        }}
      >
        Use your location, or browse by state and area.
      </Typography> */}

      {/* Row 1 — Current location */}
      <Box
        onClick={handleUseCurrentLocation}
        sx={{
          cursor: "pointer",
          width: "100%",
          bgcolor: "#fff",
          border: isCurrentActive ? "1px solid #000" : "1px solid #ddd",
          borderRadius: "12px",
          px: 2,
          py: 1.25,
          mb: 1,
          transition: "border-color 0.15s ease",
          "&:hover": { borderColor: isCurrentActive ? "#000" : "#bbb" },
        }}
      >
        <Typography
          sx={{
            fontSize: isMobile ? "16px" : "14px",
            fontWeight: 500,
            color: isCurrentActive ? "#000" : "#aaa",
            lineHeight: 1.4,
          }}
        >
          {isCurrentActive && tempCurrentArea
            ? tempCurrentArea
            : "Current location"}
        </Typography>
        <Typography sx={{ fontSize: "12px", color: "#aaa", lineHeight: 1.3 }}>
          {isCurrentActive && tempCurrentArea
            ? "Current location"
            : "Use your device location"}
        </Typography>
      </Box>

      {/* Row 2 — Search area */}
      <Box
        onClick={handleOpen}
        sx={{
          cursor: "pointer",
          width: "100%",
          bgcolor: "#fff",
          border: isManualActive ? "1px solid #000" : "1px solid #ddd",
          borderRadius: "12px",
          px: 2,
          py: 1.25,
          transition: "border-color 0.15s ease",
          "&:hover": { borderColor: isManualActive ? "#000" : "#bbb" },
        }}
      >
        <Typography
          sx={{
            fontSize: isMobile ? "16px" : "14px",
            fontWeight: 500,
            color: isManualActive ? "#000" : "#aaa",
            lineHeight: 1.4,
          }}
        >
          {isManualActive ? selectedArea : "Search area"}
        </Typography>
        <Typography sx={{ fontSize: "12px", color: "#aaa", lineHeight: 1.3 }}>
          {isManualActive
            ? selectedState || "Area selected"
            : "Select a state and area"}
        </Typography>
      </Box>

      <AreaBrowsePickerDialog
        open={open}
        onClose={handleClose}
        step={step}
        browsingState={browsingState}
        query={query}
        onQueryChange={setQuery}
        onBack={handleBack}
        onSelectState={handleSelectState}
        variant="single"
        selectedSingleArea={selectedArea}
        onPickSingleArea={handleSelectArea}
      />
    </Box>
  );
};
